/**
 * Options for formatting currency values.
 *
 * @param abbreviated - When true (default), large values use B/M/K suffixes.
 *   When false, renders the full value via Intl.NumberFormat.
 * @param minimumFractionDigits - Minimum decimal places (only when abbreviated: false). Default: 0.
 * @param maximumFractionDigits - Maximum decimal places (only when abbreviated: false). Default: 0.
 */
export interface FormatCurrencyOptions {
  abbreviated?: boolean;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

/**
 * Formats a number as a USD currency string.
 *
 * Default (abbreviated) mode:
 * - Values >= 1 billion: $X.XB
 * - Values >= 1 million: $X.XM
 * - Values >= 1 thousand: $X.XK
 * - Values < 1 thousand: $X,XXX (with locale formatting)
 * - Negative values are handled correctly: -$1.5M
 *
 * Non-abbreviated mode (abbreviated: false):
 * - Uses Intl.NumberFormat with USD currency style
 * - Respects minimumFractionDigits / maximumFractionDigits
 *
 * @param value - The numeric value to format
 * @param options - Formatting options
 * @returns Formatted currency string
 */
export const formatCurrency = (
  value: number,
  options?: FormatCurrencyOptions
): string => {
  const {
    abbreviated = true,
    minimumFractionDigits = 0,
    maximumFractionDigits,
  } = options ?? {};

  const resolvedMaximumFractionDigits =
    maximumFractionDigits ?? minimumFractionDigits;

  if (!abbreviated) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits,
      maximumFractionDigits: resolvedMaximumFractionDigits,
    }).format(value);
  }

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1_000_000_000) {
    return `${sign}$${(absValue / 1_000_000_000).toFixed(1)}B`;
  } else if (absValue >= 1_000_000) {
    return `${sign}$${(absValue / 1_000_000).toFixed(1)}M`;
  } else if (absValue >= 1_000) {
    return `${sign}$${(absValue / 1_000).toFixed(1)}K`;
  }
  return `${sign}$${absValue.toLocaleString()}`;
};
